/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import type { LucideIcon } from "lucide-react"
import { ChevronRightIcon, FileBadgeIcon, PackageIcon, SmartphoneIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link } from "react-router"

import { installerName } from "../../features/application-detail/application-apk"
import type { ApplicationApk, ThreatLevelName } from "../../features/application-detail/application-detail-types"
import { AppRoutes } from "../../routes/routes"
import { formatEnum } from "../../utils/format-enum"
import { formatTimestamp } from "../../utils/format-timestamp"
import { valueOrDash } from "../../utils/value-or-dash"
import { ClipboardValue } from "../components/clipboard-value"
import { Badge } from "../primitives/badge"
import { buttonVariants } from "../primitives/button"
import { TableCell, TableRow } from "../primitives/table"

import styles from "./application-apk-row.module.css"

/**
 * Badge classes for each threat level.
 *
 * Malware uses the semantic destructive token; the intermediate levels use
 * Tailwind's palette, while PUA stays neutral.
 */
const ThreatLevelColor = {
    MALWARE: "bg-destructive/10 text-destructive",
    HIGHLY_DANGEROUS: "bg-orange-500/10 text-orange-700",
    DANGEROUS: "bg-amber-500/10 text-amber-700",
    POTENTIALLY_UNWANTED_APP: "bg-secondary text-secondary-foreground"
} as const satisfies Record<ThreatLevelName, string>

/**
 * Returns the badge classes for a threat level.
 *
 * Unknown or missing names use the neutral style of the lowest threat level.
 *
 * @param name - Threat level name from the API.
 * @returns The badge classes.
 */
function threatLevelColor(name: string | undefined) {
    return name && Object.hasOwn(ThreatLevelColor, name)
        ? ThreatLevelColor[name as ThreatLevelName]
        : ThreatLevelColor.POTENTIALLY_UNWANTED_APP
}

interface Props {
    /**
     * APK to display.
     *
     * Removed APKs are muted and show their removal time in the installation
     * cell.
     */
    apk: ApplicationApk
}

/**
 * Renders an APK as a table row.
 *
 * Shows the application identity, affected devices, installation details,
 * signatures, categories, and threat level. Removed APKs are displayed in a
 * muted style with their removal time.
 *
 * The whole row opens the APK detail through the link in its last cell; the
 * copy buttons and tooltips sit above the link's overlay.
 *
 * @example
 * <TableBody>
 *     {apks.map((apk) => <ApplicationApkRow key={apk.id} apk={apk} />)}
 * </TableBody>
 */
export const ApplicationApkRow = observer(({ apk }: Props) => (
    <TableRow className={clsx("align-top", styles.row, { "text-muted-foreground": apk.timestampRemoved })}>
        <TableCell className="whitespace-normal">
            <div className="font-semibold wrap-anywhere">{valueOrDash(apk.name)}</div>
            <div className="font-mono text-xs wrap-anywhere text-muted-foreground">{valueOrDash(apk.packageName)}</div>
        </TableCell>

        <TableCell>
            <span className="inline-flex items-center gap-1.5 tabular-nums">
                <SmartphoneIcon className="size-4 text-muted-foreground" />
                {/* The API counts only devices the user can access. */}
                {valueOrDash(apk.affectedDevices?.toLocaleString("en-GB"))}
            </span>
        </TableCell>

        <TableCell className="whitespace-normal">
            <div className="wrap-anywhere">{valueOrDash(installerName(apk.installer))}</div>
            {/* This is when the backend first linked the APK to the device; the SDK does not report install time. */}
            <div className="text-muted-foreground tabular-nums">
                {valueOrDash(formatTimestamp(apk.timestampCreated))}
            </div>
            {apk.timestampRemoved && (
                <div className="tabular-nums">Removed {valueOrDash(formatTimestamp(apk.timestampRemoved))}</div>
            )}
        </TableCell>

        <TableCell>
            <dl className="m-0 grid grid-cols-[auto_1fr] items-center gap-x-1.5">
                {renderSignature(FileBadgeIcon, "Certificate signature (SHA-1)", apk.certificateSignature)}
                {renderSignature(PackageIcon, "APK signature (SHA-256)", apk.apkSignature)}
            </dl>
        </TableCell>

        <TableCell className="whitespace-normal">{renderCategories(apk)}</TableCell>

        <TableCell>
            <Badge className={threatLevelColor(apk.threatLevel?.name)}>
                {valueOrDash(formatEnum(apk.threatLevel?.name))}
            </Badge>
        </TableCell>

        <TableCell>
            <Link
                to={AppRoutes.ApkDetail.replace(":id", `${apk.id}`)}
                className={clsx(buttonVariants({ variant: "outline", size: "sm" }), styles.link)}
                // Every row's link reads `Detail`, so the APK names each one.
                aria-label={`View detail of ${apk.name}`}
            >
                Detail
                <ChevronRightIcon data-icon="inline-end" />
            </Link>
        </TableCell>
    </TableRow>
))

/**
 * Renders a signature as a label/value pair.
 *
 * The icon replaces the visible label; the full label is available as both a
 * tooltip and screen-reader text.
 *
 * @param Icon - Icon representing the signature.
 * @param label - Signature name and algorithm.
 * @param value - Signature to display and copy.
 * @returns The `<dt>` and `<dd>` for the signature.
 */
function renderSignature(Icon: LucideIcon, label: string, value: string | undefined) {
    return (
        <>
            <dt title={label} className={styles.promote}>
                <Icon className="size-4" />
                <span className="sr-only">{label}</span>
            </dt>
            <dd className={clsx("m-0 font-mono text-xs", styles.promoteContent)}>
                <ClipboardValue value={value} />
            </dd>
        </>
    )
}

/**
 * Renders malware types and families as visually distinct badges.
 *
 * Types use filled badges and families use outlined badges. Screen-reader-only
 * prefixes distinguish them when a type and family have the same name.
 *
 * @param apk - APK whose categories are displayed.
 * @returns The category badges, or a dash when none are available.
 */
function renderCategories({ malwareTypes = [], malwareFamilies = [] }: ApplicationApk) {
    if (malwareTypes.length === 0 && malwareFamilies.length === 0) return "—"

    return (
        <div className="flex flex-wrap gap-1">
            {/* Prefix the keys to avoid collisions between types and families with the same name. */}
            {malwareTypes.map((type) => (
                <Badge key={`type-${type}`} variant="secondary" title="Malware type" className={styles.promote}>
                    <span className="sr-only">Type: </span>
                    {type}
                </Badge>
            ))}
            {malwareFamilies.map((family) => (
                <Badge key={`family-${family}`} variant="outline" title="Malware family" className={styles.promote}>
                    <span className="sr-only">Family: </span>
                    {family}
                </Badge>
            ))}
        </div>
    )
}
